import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { BadgeCheck, Loader2, Save, Phone } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from '../../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

const EscSettingsTab = ({ isViewer = false }) => {
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    let mounted = true;
    axios.get(`${API}/api/settings/esc-accreditation`, { headers })
      .then(r => { if (mounted) setPhone(r.data?.current?.company_phone || ''); })
      .catch(() => { if (mounted) setPhone(''); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/api/settings/esc-accreditation`,
        { company_phone: phone || '' },
        { headers }
      );
      toast({ title: 'ESC settings saved', description: 'Future ESC accreditation lists will use this phone number.' });
    } catch (e) {
      toast({
        title: 'Save failed',
        description: e?.response?.data?.detail || 'Could not save settings',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="esc-settings-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <BadgeCheck className="h-5 w-5 text-red-500" />
          ESC Accreditation
        </CardTitle>
        <p className="text-xs text-gray-500">
          Configure the company telephone shown on every row of the ESC Accreditation list (Staff Directory → ESC tab + PDF/Excel exports).
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-gray-500 text-sm">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading current setting…
          </div>
        ) : (
          <div className="space-y-3 max-w-md">
            <div>
              <Label className="text-xs flex items-center gap-1.5">
                <Phone className="h-3.5 w-3.5 text-gray-500" />
                Company Telephone Number
              </Label>
              <Input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+97125650104"
                disabled={isViewer}
                data-testid="esc-company-phone-input"
                className="font-mono"
              />
              <p className="text-[11px] text-gray-500 mt-1">
                This number auto-fills the "Company Tel. Number" column for every active staff row.
              </p>
            </div>
            <div className="flex justify-end">
              <Button
                onClick={save}
                disabled={saving || isViewer}
                className="bg-red-500 hover:bg-red-600 gap-1.5"
                data-testid="esc-settings-save"
              >
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                Save
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EscSettingsTab;
